import { WarningCycleIcon } from "@/components/common/icons/WarningCycleIcon/WarningCycleIcon"
import { Typography } from "@/components/common/Typography/Typography"
import { AnswerSourceGroup } from "@/components/feature/AnswerSourceGroup/AnswerSourceGroup"
import { TextAreaListItem } from "@/components/feature/CollapsibleQuestionnaireList/TextAreaListItem/TextAreaListItem"
import { AskForHelpModal } from "@/components/feature/Modals/AskForHelpModal/AskForHelpModal"
import { Typography as ShadcnTypography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AnswerMemorySuggestion } from "@/pages/questionnaire/AnswerMemorySuggestion"
import { UpdateExcelCellFunctionType } from "@/pages/questionnaire/ExcelAnswersPage/useExcelCellSync"
import { QuestionAnswerCard } from "@/pages/questionnaire/QuestionAnswerCard"
import {
  getQuestionAnswerState,
  getQuestionAnswerTone,
  hasQuestionAnswerResponse,
  needsQuestionAnswerHelp,
} from "@/pages/questionnaire/questionAnswerState"
import {
  ChangeActionPayload,
  EditResponsePairKey,
  TextAreaStateAction,
  TextAreaStateActionType,
} from "@/types/hooks/useTextAreaStates"
import {
  AnswerTarget,
  Citation,
  QuestionnaireResponsePair,
} from "@/types/questionnaire/models"
import { classNames } from "@/utils/classNames"
import { debounce, type DebouncedFunction } from "@/utils/optimisationUtils"
import { IconMessage } from "@tabler/icons-react"
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useLocation } from "react-router"
import { OptionClearSelectionButton } from "../PdfAnswerItem/OptionClearSelectionButton"
import { CheckBoxInputOption } from "./CheckBoxInputOption"
import { RadioInputOption } from "./RadioInputOption"

interface AnswerItemProps {
  responsePair: QuestionnaireResponsePair
  sectionId: string
  updateExcelCell: UpdateExcelCellFunctionType
  updateDataToApi: (action: TextAreaStateAction) => void
  /** Registers a callback that forces this item's pending debounced save to run now. */
  registerFlush?: (responsePairId: string, flush: () => void) => void
  unregisterFlush?: (responsePairId: string) => void
  isHighlighted?: boolean
  disableSources?: boolean
  disableTextResize?: boolean
}

const EDIT_RESPONSE_PAIR_KEY: EditResponsePairKey = "response"
const TEXT_UPDATE_DELAY_MILISEC = 1000
const DEMO_FIRST_ANSWER_SOURCE: Citation = {
  sourceFileId: "demo-annual-report",
  sourceFileName: "Annual Report.pdf",
  sourceFilePage: 1,
}

export const ExcelAnswerItem: React.FC<AnswerItemProps> = ({
  responsePair,
  sectionId,
  // updateText,
  updateDataToApi,
  updateExcelCell,
  registerFlush,
  unregisterFlush,
  isHighlighted,
  disableSources = false,
  disableTextResize = false,
}) => {
  const { translate } = useTranslation()
  const query = new URLSearchParams(useLocation().search)
  const urlQueryId = query.get("question")
  const [isAskForHelpModalOpen, setIsAskForHelpModalOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const handleOpenShareModal = () => {
    setIsAskForHelpModalOpen(true)
  }

  const initialText = responsePair[EDIT_RESPONSE_PAIR_KEY] ?? ""
  const [text, setText] = useState(initialText)
  const hasBeenEdited = useRef(false)

  // Keep latest values in refs so a stable debounced function can always use fresh data
  // without needing a useEffect to rebuild it.
  const latestRef = useRef({
    responsePair,
    sectionId,
    updateDataToApi,
    updateExcelCell,
  })

  latestRef.current = {
    responsePair,
    sectionId,
    updateDataToApi,
    updateExcelCell,
  }

  const debouncedPersistFreeTextRef = useRef<DebouncedFunction<
    [string]
  > | null>(null)

  if (!debouncedPersistFreeTextRef.current) {
    debouncedPersistFreeTextRef.current = debounce((textToPersist: string) => {
      // Mimic previous behavior: don't persist initial value unless user edited.
      if (!hasBeenEdited.current) {
        return
      }

      const { responsePair, sectionId, updateDataToApi, updateExcelCell } =
        latestRef.current

      if (responsePair.type !== "free-text") {
        return
      }

      const cellAddress = responsePair.answerTargetsJson?.[0]?.addr
      if (cellAddress && responsePair.sheetName) {
        updateExcelCell(responsePair.sheetName, cellAddress, textToPersist)
      }

      updateDataToApi({
        type: TextAreaStateActionType.Change,
        payload: {
          sectionId: sectionId,
          responsePairId: responsePair.id,
          text: textToPersist,
        } satisfies ChangeActionPayload,
      })
    }, TEXT_UPDATE_DELAY_MILISEC)
  }

  // Lets the page force this item's pending debounced save to run right now
  // (e.g. before a "regenerate answers" redirect), instead of waiting out the
  // debounce or losing the edit.
  useEffect(() => {
    if (!registerFlush || !unregisterFlush) {
      return
    }

    const id = responsePair.id
    registerFlush(id, () => {
      debouncedPersistFreeTextRef.current?.flush()
    })

    return () => {
      // An item can unmount (e.g. filtered out of visibleResponsePairs) before
      // its 1s debounce fires. Flush the pending save immediately so the edit
      // still lands — the underlying save is tracked in the page-level
      // in-flight set via the wrapped mutate, so flushPendingAnswerEdits still
      // awaits it even though this item is already gone by the time it runs.
      debouncedPersistFreeTextRef.current?.flush()
      unregisterFlush(id)
    }
  }, [registerFlush, unregisterFlush, responsePair.id])

  const onTextChange = useCallback((changedText: string) => {
    hasBeenEdited.current = true
    setText(changedText)

    debouncedPersistFreeTextRef.current?.(changedText)
  }, [])

  const handleRadioOptionChange = useCallback(
    (value: string, cellAddress: string) => {
      hasBeenEdited.current = true
      updateDataToApi({
        type: TextAreaStateActionType.Change,
        payload: {
          sectionId: sectionId,
          responsePairId: responsePair.id,
          text: value,
        },
      })

      if (responsePair.sheetName) {
        updateExcelCell(responsePair.sheetName, cellAddress, value)
      }

      setText(value)
    },
    [
      responsePair.id,
      responsePair.sheetName,
      sectionId,
      updateDataToApi,
      updateExcelCell,
    ],
  )

  const handleCheckboxChange = useCallback(
    (apiValue: string, excelCellValue: string, cellAddress: string) => {
      hasBeenEdited.current = true
      if (responsePair.sheetName) {
        updateExcelCell(responsePair.sheetName, cellAddress, excelCellValue)
      }

      updateDataToApi({
        type: TextAreaStateActionType.Change,
        payload: {
          sectionId: sectionId,
          responsePairId: responsePair.id,
          text: apiValue,
        },
      })
      setText(apiValue)
    },
    [
      responsePair.id,
      responsePair.sheetName,
      sectionId,
      updateDataToApi,
      updateExcelCell,
    ],
  )

  const handleHighlightCell = () => {
    const cellAddress = responsePair.answerTargetsJson?.[0]?.addr
    if (cellAddress && responsePair.sheetName) {
      updateExcelCell(responsePair.sheetName, cellAddress, text, true)
    }
  }

  const handleClearFreeText = useCallback(() => {
    if (responsePair.type !== "free-text") {
      return
    }

    hasBeenEdited.current = true
    setText("")

    const cellAddress = responsePair.answerTargetsJson?.[0]?.addr
    if (cellAddress && responsePair.sheetName) {
      updateExcelCell(responsePair.sheetName, cellAddress, "")
    }

    updateDataToApi({
      type: TextAreaStateActionType.Change,
      payload: {
        sectionId,
        responsePairId: responsePair.id,
        text: "",
      },
    })
  }, [
    responsePair.answerTargetsJson,
    responsePair.id,
    responsePair.sheetName,
    responsePair.type,
    sectionId,
    updateDataToApi,
    updateExcelCell,
  ])

  const handleClearRadioOption = useCallback(
    (cellAddress: string) => {
      handleRadioOptionChange("", cellAddress)
    },
    [handleRadioOptionChange],
  )

  const handleClearCheckboxOption = useCallback(
    (cellAddress: string, answerTarget: AnswerTarget) => {
      const selectedLabel = answerTarget.label
      let parsedSelection: string[]

      try {
        parsedSelection = text.length ? JSON.parse(text) : []
      } catch {
        parsedSelection = []
      }

      const nextSelection = parsedSelection.filter(
        (label) => label !== selectedLabel,
      )
      const apiValue = JSON.stringify(nextSelection)
      const negativeValueForExcel = answerTarget.uncheckedValue || ""

      handleCheckboxChange(apiValue, negativeValueForExcel, cellAddress)
    },
    [handleCheckboxChange, text],
  )

  // FREE TEXT ONLY, deliberately. A boolean or multiple-choice answer is written as one of
  // the cell's allowed values, and a remembered sentence is not one of them — offering it
  // there would put an invalid option into the workbook.
  const memorySuggestion =
    responsePair.memory &&
    (responsePair.type || "free-text") === "free-text" ? (
      <AnswerMemorySuggestion
        responsePairId={responsePair.id}
        questionnaireId={responsePair.questionnaire}
        suggestion={responsePair.memory}
      />
    ) : null

  const handleClearQuestionValue = useCallback(() => {
    const questionType = responsePair.type || "free-text"

    if (questionType === "free-text") {
      handleClearFreeText()
      return
    }

    if (questionType === "boolean" || questionType === "multiple-choice") {
      const answerTargets = responsePair.answerTargetsJson || []
      const selectedAnswerTarget = answerTargets.find((answerTarget) =>
        answerTarget?.allowedValues?.includes(text),
      )
      const fallbackTarget = answerTargets[0]
      const targetToClear = selectedAnswerTarget || fallbackTarget

      if (targetToClear?.addr) {
        handleClearRadioOption(targetToClear.addr)
      }

      return
    }

    if (questionType === "multiple-select") {
      const answerTargets = responsePair.answerTargetsJson || []

      answerTargets.forEach((answerTarget) => {
        if (!answerTarget?.addr || !responsePair.sheetName) {
          return
        }

        const excelValue =
          answerTarget.semantics === "CheckboxOption"
            ? answerTarget.uncheckedValue || ""
            : ""

        updateExcelCell(responsePair.sheetName, answerTarget.addr, excelValue)
      })

      hasBeenEdited.current = true
      setText("[]")
      updateDataToApi({
        type: TextAreaStateActionType.Change,
        payload: {
          sectionId,
          responsePairId: responsePair.id,
          text: "[]",
        },
      })
    }
  }, [
    handleClearFreeText,
    handleClearRadioOption,
    responsePair.answerTargetsJson,
    responsePair.id,
    responsePair.sheetName,
    responsePair.type,
    sectionId,
    text,
    updateDataToApi,
    updateExcelCell,
  ])

  const isClearButtonDisabled = useMemo(() => {
    const questionType = responsePair.type || "free-text"

    if (questionType === "free-text") {
      return !text || text.trim().length === 0
    }

    if (questionType === "multiple-select") {
      try {
        const parsedSelection = text.length ? JSON.parse(text) : []
        return !Array.isArray(parsedSelection) || parsedSelection.length === 0
      } catch {
        return true
      }
    }

    return !text || text.length === 0
  }, [responsePair.type, text])

  const highlightElementId = isHighlighted ? "first-answer-field" : undefined
  const sourceElementId = isHighlighted
    ? "first-answer-source-field"
    : undefined
  const displayCitations = useMemo(() => {
    const existingCitations = responsePair.citations ?? []

    if (!isHighlighted) {
      return existingCitations
    }

    if (existingCitations.length > 0) {
      return existingCitations.map((citation, index) => {
        if (index !== 0) {
          return citation
        }

        return {
          ...citation,
          sourceFileId:
            citation.sourceFileId || DEMO_FIRST_ANSWER_SOURCE.sourceFileId,
          sourceFileName:
            citation.sourceFileName || DEMO_FIRST_ANSWER_SOURCE.sourceFileName,
          sourceFilePage:
            citation.sourceFilePage || DEMO_FIRST_ANSWER_SOURCE.sourceFilePage,
        }
      })
    }

    return [{ ...DEMO_FIRST_ANSWER_SOURCE }]
  }, [isHighlighted, responsePair.citations])
  const answerState = getQuestionAnswerState(responsePair)
  const answerTone = getQuestionAnswerTone(responsePair)
  const hasResponse = hasQuestionAnswerResponse(responsePair)

  const renderInput = () => {
    const questionType = responsePair.type || "free-text"
    const answerTargets = responsePair.answerTargetsJson || []

    const hasError = !hasResponse
    const hasWarning = answerState === "low-confidence"

    let inputElement: ReactNode

    switch (questionType) {
      case "boolean":
      case "multiple-choice":
        inputElement = (
          <RadioInputOption
            answerTargets={answerTargets}
            value={text}
            onChange={handleRadioOptionChange}
            onClear={handleClearRadioOption}
            hasError={hasError}
            hasWarning={hasWarning}
            responsePairId={responsePair.id}
          />
        )
        break

      case "multiple-select":
        inputElement = (
          <CheckBoxInputOption
            answerTargets={answerTargets}
            value={text}
            onChange={handleCheckboxChange}
            onClear={handleClearCheckboxOption}
            hasError={hasError}
            hasWarning={hasWarning}
            responsePairId={responsePair.id}
          />
        )
        break

      case "free-text":
      default:
        inputElement = (
          <TextAreaListItem
            text={text}
            responsePair={responsePair}
            onTextChange={onTextChange}
            editResponsePairKey={EDIT_RESPONSE_PAIR_KEY}
            onFocus={handleHighlightCell}
            elementId={highlightElementId}
            isResizeLocked={disableTextResize}
          />
        )
        break
    }

    return inputElement
  }

  const statusMessage = !hasResponse ? (
    <div className="flex items-center gap-XS">
      <WarningCycleIcon className="text-redLight" />
      <Typography variant="textXS" className="text-redLight">
        {translate("QUESTIONNAIRE_ANSWERS/WARNING_NO_GENERATED_ANSWER")}
      </Typography>
    </div>
  ) : answerState === "low-confidence" ? (
    <div className="flex items-center gap-XS">
      <WarningCycleIcon className="text-warning" />
      <Typography variant="textXS" className="text-warning">
        {translate("QUESTIONNAIRE_ANSWERS/WARNING_LOW_CONFIDENCE_ANSWER")}
      </Typography>
    </div>
  ) : null

  const statusAction = needsQuestionAnswerHelp(responsePair) ? (
    <div
      onClick={handleOpenShareModal}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleOpenShareModal()
        }
      }}
      className={classNames("hover:cursor-pointer")}
      role="button"
      tabIndex={0}
    >
      <IconMessage className="text-grey5 hover:text-white" stroke={1} />
    </div>
  ) : null

  // The suggestion sits ABOVE the sources: it is a thing to act on, they are a reference.
  const footer =
    memorySuggestion || hasResponse ? (
      <div className="flex flex-col gap-2">
        {memorySuggestion}
        {hasResponse ? (
          <AnswerSourceGroup
            citations={displayCitations}
            elementId={sourceElementId}
            disableSourceClicks={disableSources}
            forceOpen={disableSources}
          />
        ) : null}
      </div>
    ) : null

  return (
    <>
      {isAskForHelpModalOpen && (
        <AskForHelpModal
          responsePairId={responsePair.id}
          setIsAskForHelpModalOpen={setIsAskForHelpModalOpen}
        />
      )}

      <div
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsFocused(false)
          }
        }}
      >
        <QuestionAnswerCard
          id={responsePair.id}
          tone={answerTone}
          isActive={isFocused}
          className={classNames(
            urlQueryId === responsePair.id ? "animate-highlight" : "",
            isHighlighted
              ? "shadow-[inset_0_0_0_2px_hsl(var(--primary)/0.6)]"
              : "",
          )}
          header={
            <ShadcnTypography
              as="small"
              className="font-medium leading-snug text-white/80"
            >
              {responsePair.question}
            </ShadcnTypography>
          }
          headerAction={
            <OptionClearSelectionButton
              ariaLabel={translate("QUESTIONNAIRE_ANSWERS/CLEAR_SELECTION")}
              disabled={isClearButtonDisabled}
              onClear={handleClearQuestionValue}
            />
          }
          body={renderInput()}
          statusMessage={statusMessage}
          statusAction={statusAction}
          footer={footer}
        />
      </div>
    </>
  )
}
